// Make vite aware of favicons, fonts and external js snippets referenced in <head> and css
import.meta.glob(['../favicons/**', '../fonts/**', '../integrations/**']);

import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { DefineComponent, createApp, h } from 'vue';
import Vapor from 'laravel-vapor';
import { createPinia } from 'pinia';
import { defineRule, configure } from 'vee-validate';
import { required, email, confirmed, min, regex, url } from '@vee-validate/rules';
import VueTippy from 'vue-tippy';
import { ZiggyVue } from 'ziggy-js';
import * as Sentry from '@sentry/vue';
import { VueQueryPlugin } from '@tanstack/vue-query';

import { i18nInstance } from '~/utils/localization';

import './bootstrap';

import '../css/app.css';
import '../css/dancingscript.css';
import '../css/kalam.css';
import '../css/inter.css';

import { directiveCy, directiveCySafe } from './directives';
import resetStore from './stores/resetStore';
import { queryClient } from './queries';
import { Default } from './layouts';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'fynk';

//Deactivate Darkmode until it is fully implemented
/*
if (localStorage.getItem('darkMode') === 'true') {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}*/

// Set default to light mode
if (localStorage.getItem('darkMode') !== 'true') {
  document.documentElement.classList.remove('dark');
} else {
  document.documentElement.classList.add('dark');
}

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: async (name) => {
    const page = await (resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob('./Pages/**/*.vue')
    ) as Promise<DefineComponent>);

    const pageLayout = page.default.layout;

    if (!pageLayout) {
      page.default.layout = Default;
      return page;
    }

    if (Array.isArray(pageLayout)) {
      page.default.layout = [Default, ...pageLayout];
      return page;
    }

    if (typeof pageLayout === 'function') {
      page.default.layout = (_h: typeof h, page: DefineComponent) => _h(Default, () => _h(pageLayout, page));
      return page;
    }

    return page;
  },
  setup({ el, App, props, plugin }) {
    const pinia = createPinia();
    pinia.use(resetStore);

    const VueApp = createApp({ render: () => h(App, props) });

    VueApp.use(plugin)
      .use(VueQueryPlugin, {
        enableDevtoolsV6Plugin: true,
        queryClient,
      })
      .use(ZiggyVue)
      .use(pinia)
      .use(i18nInstance)
      .use(VueTippy, {
        arrow: true,
        directive: 'tippy', // => v-tippy
        component: 'Tippy', // => <Tippy/>
        componentSingleton: 'TippySingleton', // => <TippySingleton />,
      })
      .directive('cy', directiveCy)
      .directive('cy-safe', directiveCySafe);

    if (window.location.origin === 'https://app.fynk.com' || window.location.origin === 'https://staging.fynk.com') {
      Sentry.init({
        app: VueApp,
        dsn: 'https://5744ac350dd2104d588e1fcf100af284@o4507409537695744.ingest.de.sentry.io/4507409547198544',
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,

        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: 0.1,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,

        integrations: [
          Sentry.replayIntegration(),
          Sentry.captureConsoleIntegration({
            levels: ['error'],
          }),
          Sentry.vueIntegration({
            app: VueApp,
            attachProps: true,
            tracingOptions: {
              trackComponents: true,
              timeout: 1000,
            },
          }),
        ],

        environment: window.location.origin === 'https://app.fynk.com' ? 'production' : 'staging',
      });
    }

    VueApp.mixin({
      methods: {
        asset: window.Vapor.asset,
      },
    });

    VueApp.mount(el);

    return VueApp;
  },
  progress: {
    color: '#4338ca',
  },
});

// initialize vapor
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;
window.Vapor = Vapor;
window.Vapor.withBaseAssetUrl(import.meta.env.VITE_VAPOR_ASSET_URL);

// global validation rules
defineRule('required', required);
defineRule('email', email);
defineRule('confirmed', confirmed);
defineRule('min', min);
defineRule('regex', regex);
defineRule('url', url);

const { t } = i18nInstance.global;

configure({
  generateMessage: (context) => {
    let message = t('validation.invalid', { fieldName: t('formFields.' + context.field) });
    if (context.rule?.name === 'required')
      message = t('validation.required', { fieldName: t('formFields.' + context.field) });
    if (context.rule?.name === 'email')
      message = t('validation.email', { fieldName: t('formFields.' + context.field) });
    if (context.rule?.name === 'confirmed')
      message = t('validation.confirmed', { fieldName: t('formFields.' + context.field) });
    if (context.rule?.name === 'min') message = t('validation.min', { fieldName: t('formFields.' + context.field) });
    return message;
  },
});

// Prevent back button from showing outdated pages
window.addEventListener('popstate', (event: PopStateEvent) => {
  // no special handling if external token param exists
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (!!urlParams.get('token')) {
    return;
  }

  event.stopImmediatePropagation();

  // On mere hash changes, do not force router reload
  if (event.state === null) {
    event.preventDefault();
    return false;
  }

  router.reload({
    replace: true,
    onError: () => (window.location.href = event.state.url),
  });
});

/** CONTAINER QUERY POLYFILL FOR OLDER BROWSER VERSIONS */
// Support Test
const supportsContainerQueries = 'container' in document.documentElement.style;

// Conditional Import
if (!supportsContainerQueries) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require('container-query-polyfill');
}

// eslint-disable-next-line
const tailwindSafeList = [
  'scale-75',
  'scale-80',
  'scale-85',
  'scale-90',
  'scale-95',
  'scale-100',

  'sm:hidden',
  'md:hidden',
  'lg:hidden',
  'xl:hidden',
  '2xl:hidden',
  '3xl:hidden',
  '4xl:hidden',
  '5xl:hidden',
  '@sm:hidden',
  '@md:hidden',
  '@lg:hidden',
  '@xl:hidden',
  '@2xl:hidden',
  '@3xl:hidden',
  '@4xl:hidden',
  '@5xl:hidden',
  '@6xl:hidden',
  '@7xl:hidden',
  '@8xl:hidden',
  '@9xl:hidden',

  'sm:table-cell',
  'md:table-cell',
  'lg:table-cell',
  'xl:table-cell',
  '2xl:table-cell',
  '3xl:table-cell',
  '4xl:table-cell',
  '5xl:table-cell',
  '@sm:table-cell',
  '@md:table-cell',
  '@lg:table-cell',
  '@xl:table-cell',
  '@2xl:table-cell',
  '@3xl:table-cell',
  '@4xl:table-cell',
  '@5xl:table-cell',
  '@6xl:table-cell',
  '@7xl:table-cell',
  '@8xl:table-cell',
  '@9xl:table-cell',

  'sm:inline-flex',
  'md:inline-flex',
  'lg:inline-flex',
  'xl:inline-flex',
  '2xl:inline-flex',
  '3xl:inline-flex',
  '4xl:inline-flex',
  '5xl:inline-flex',
  '@sm:inline-flex',
  '@md:inline-flex',
  '@lg:inline-flex',
  '@xl:inline-flex',
  '@2xl:inline-flex',
  '@3xl:inline-flex',
  '@4xl:inline-flex',
  '@5xl:inline-flex',
  '@6xl:inline-flex',
  '@7xl:inline-flex',
  '@8xl:inline-flex',
  '@9xl:inline-flex',

  'grid-cols-1',
  'grid-cols-2',
  'grid-cols-3',
  'grid-cols-4',
  'grid-cols-5',
  'grid-cols-6',
  'grid-cols-7',
  'grid-cols-8',
  'grid-cols-9',
  'grid-cols-10',
  'grid-cols-11',
  'grid-cols-12',
  'grid-cols-13',
  'grid-cols-14',

  '-translate-x-[100%]',
  '-translate-x-[200%]',
  '-translate-x-[300%]',
  '-translate-x-[400%]',
  '-translate-x-[500%]',
  '-translate-x-[600%]',
  '-translate-x-[700%]',

  'translate-x-[100%]',
  'translate-x-[200%]',
  'translate-x-[300%]',
  'translate-x-[400%]',
  'translate-x-[500%]',
  'translate-x-[600%]',
  'translate-x-[700%]',

  'delay-0',
  'delay-50',
  'delay-100',
  'delay-150',
  'delay-200',
  'delay-250',
  'delay-300',
];
